import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import useAuth from '../hooks/useAuth';
import ApiService from '../services/api';
import AdminVisitorsTab from './AdminVisitorsTab';
import AdminFeatureFlagsTab from './AdminFeatureFlagsTab';
import AdminPluginsTab from './AdminPluginsTab';
import AdminAiProviderTab from './admin/AdminAiProviderTab';
import AICompanionModal from './test-manager/modals/AICompanionModal';
import ArtifactViz from './charts/ArtifactViz';
import SignupFireworks from './admin/SignupFireworks';
import { useToast } from './ToastProvider';

const ADMIN_AUTO_REFRESH_MS = 30_000;
const ADMIN_SIGNUP_POLL_MS = 8_000;

const tenantKey = (t) => String(t?._id || t?.id || '');
const shortRoute = (route) => String(route || '')
  .replace(/\/api\//, '/')
  .replace(/\/[0-9a-fA-F]{24}(?=\/|$)/g, '/:id')
  .slice(0, 44);

const formatUptime = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
};

const OpsStat = ({ label, value, hint, tone = 'default' }) => {
  const toneClass = tone === 'bad'
    ? 'text-rose-300'
    : tone === 'warn' ? 'text-amber-300' : tone === 'good' ? 'text-teal-300' : 'text-slate-100';
  return (
    <div className="p-3 rounded border border-white/10 bg-white/[0.04]">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div> : null}
    </div>
  );
};

const PLAN_VALUES = new Set(['free', 'starter', 'advanced', 'enterprise']);

const normalizeTenants = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map((t) => ({
    ...t,
    _id: tenantKey(t),
    plan: PLAN_VALUES.has(String(t?.plan || '').toLowerCase())
      ? String(t.plan).toLowerCase()
      : 'free',
    seats: Math.max(1, parseInt(t?.seats, 10) || 1),
    features: t?.features && typeof t.features === 'object' ? { ...t.features } : {},
  }));
};

const mergeTenantUpdate = (list, tenantId, updated, fallbackPatch = null) => {
  const id = String(tenantId);
  if (!id) return list;
  return list.map((t) => {
    if (tenantKey(t) !== id) return t;
    // Ignore empty/non-tenant API bodies so a bad response can't wipe the row.
    const hasValidUpdate = updated && typeof updated === 'object' && tenantKey(updated);
    const source = hasValidUpdate ? updated : { ...t, ...(fallbackPatch || {}) };
    const nextPlan = String(source.plan ?? t.plan ?? 'free').toLowerCase();
    return {
      ...t,
      ...source,
      _id: tenantKey(source) || tenantKey(t),
      plan: PLAN_VALUES.has(nextPlan) ? nextPlan : (t.plan || 'free'),
      seats: Math.max(1, parseInt(source.seats ?? t.seats, 10) || 1),
      features: source.features && typeof source.features === 'object'
        ? { ...(t.features || {}), ...source.features }
        : (t.features || {}),
    };
  });
};

const AdminPortal = () => {
  const {
    isAuthenticated, currentUser, login, logout,
    setCurrentUser, setIsAuthenticated,
  } = useAuth();
  const { showToast } = useToast();

  // Admin always uses dark surface tokens (bg-surface-1 etc.). Without .dark on
  // <html>, those resolve to :root white and light text disappears on panels.
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    root.classList.add('dark');
    return () => {
      if (!hadDark) root.classList.remove('dark');
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [fireworksActive, setFireworksActive] = useState(false);
  const signupSinceRef = useRef(null);
  const seenSignupIdsRef = useRef(new Set());
  const knownTenantIdsRef = useRef(null);
  const refreshInFlightRef = useRef(false);
  const celebrateSignupsRef = useRef(() => {});

  const celebrateSignups = useCallback((signups) => {
    const fresh = (Array.isArray(signups) ? signups : []).filter((s) => {
      const id = String(s?.id || '');
      if (!id || seenSignupIdsRef.current.has(id)) return false;
      seenSignupIdsRef.current.add(id);
      return true;
    });
    if (!fresh.length) return;
    const newest = fresh[0];
    const who = newest.email || newest.company || 'someone';
    const where = newest.company && newest.email ? ` · ${newest.company}` : '';
    showToast({
      type: 'success',
      title: fresh.length === 1 ? 'New signup 🎉' : `${fresh.length} new signups 🎉`,
      message: fresh.length === 1
        ? `${who}${where}`
        : fresh.slice(0, 3).map((s) => s.email || s.company || 'signup').join(', '),
      duration: 15000,
    });
    setFireworksActive(true);
  }, [showToast]);
  celebrateSignupsRef.current = celebrateSignups;

  // Login MFA challenge
  const [mfaStep, setMfaStep] = useState(null); // null | 'setup' | 'code'
  const [mfaToken, setMfaToken] = useState('');
  const [mfaQr, setMfaQr] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);

  // Security tab
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdNew2, setPwdNew2] = useState('');
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdMsg, setPwdMsg] = useState('');
  const [sec2faQr, setSec2faQr] = useState('');
  const [sec2faSecret, setSec2faSecret] = useState('');
  const [sec2faCode, setSec2faCode] = useState('');
  const [sec2faBusy, setSec2faBusy] = useState(false);
  const [sec2faMsg, setSec2faMsg] = useState('');
  const [showCompanion, setShowCompanion] = useState(false);

  const [tenants, setTenants] = useState([]); // accounts
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [savingByTenantId, setSavingByTenantId] = useState({});

  // NEW: billing/subscription panel state
  const [seatUsageByTenantId, setSeatUsageByTenantId] = useState({});
  const [billingBusy, setBillingBusy] = useState(false);
  const [tempAccessDays, setTempAccessDays] = useState(7);
  const [seatLimitDraft, setSeatLimitDraft] = useState('');

  const PLAN_OPTIONS = [
    { value: 'free', label: 'Free' },
    { value: 'starter', label: 'Starter' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'enterprise', label: 'Enterprise' }
  ];

  const isSuperAdmin = !!(
    currentUser?.isSuperAdmin
    || String(currentUser?.email || '').toLowerCase() === 'admin@testera.io'
  );

  const normPlan = (t) => {
    const p = String(t?.plan || 'free').toLowerCase();
    if (p === 'team') return 'advanced';
    return PLAN_VALUES.has(p) ? p : 'free';
  };

  const fetchTenants = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    if (!quiet) setError('');
    try {
      const list = await ApiService.getTenants?.(); // expected backend endpoint
      const normalized = normalizeTenants(list);
      const nextIds = new Set(normalized.map((t) => tenantKey(t)).filter(Boolean));
      // Celebrate brand-new companies even if audit/user poll missed them.
      if (knownTenantIdsRef.current) {
        const freshTenants = normalized.filter((t) => {
          const id = tenantKey(t);
          return id && !knownTenantIdsRef.current.has(id);
        });
        if (freshTenants.length) {
          celebrateSignupsRef.current(freshTenants.map((t) => ({
            id: `tenant:${tenantKey(t)}`,
            at: t.createdAt || new Date().toISOString(),
            email: '',
            company: t.name || t.slug || 'New company',
            tenantSlug: t.slug || '',
            description: 'New company signup',
          })));
        }
      }
      knownTenantIdsRef.current = nextIds;
      setTenants(normalized);
      setSelectedTenant((prev) => {
        if (!prev) return prev;
        const id = tenantKey(prev);
        return normalized.find((t) => tenantKey(t) === id) || prev;
      });
      setLastRefreshedAt(new Date());
      return normalized;
    } catch (e) {
      if (!quiet) setError(e?.message || 'Failed to load accounts');
      return [];
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  const fetchTenantUsers = async (tenantId) => {
    setLoading(true);
    setError('');
    try {
      const list = await ApiService.getUsersByTenant?.(tenantId);
      setTenantUsers(Array.isArray(list) ? list : []);
      // NEW: also refresh seat usage when selecting a tenant
      fetchSeatUsage(tenantId).catch(()=>{});
    } catch (e) {
      setError(e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchSeatUsage = async (tenantId) => {
    if (!tenantId || !ApiService.getTenantSeatUsage) return null;
    try {
      const data = await ApiService.getTenantSeatUsage(tenantId);
      setSeatUsageByTenantId(prev => ({ ...prev, [String(tenantId)]: data }));
      return data;
    } catch (e) {
      setError(e?.message || 'Failed to load seat usage');
      return null;
    }
  };

  const refreshSelectedTenantPanels = async (tenant) => {
    if (!tenant?._id) return;
    await Promise.all([
      fetchTenantUsers(tenant._id),
      fetchSeatUsage(tenant._id)
    ]);
  };

  const handleToggleBlock = async (tenant) => {
    const tenantId = tenantKey(tenant);
    if (!tenantId) return;
    try {
      const updated = await ApiService.updateTenant?.(tenantId, { blocked: !tenant.blocked });
      applyTenantServerUpdate(tenantId, updated, { blocked: !tenant.blocked });
      flashSavedTenant(tenantId);
    } catch (e) {
      setError(e?.message || 'Failed to update block');
    }
  };

  // NOTE: Feature flags are managed via curated controls (updateTenantFeaturesCurated)
  // ...remove legacy dynamic toggle helper...

  const isSavingTenant = (tenantId) => !!savingByTenantId?.[String(tenantId)];
  const setSavingTenant = (tenantId, saving) => {
    const key = String(tenantId || '');
    if (!key) return;
    setSavingByTenantId(prev => ({ ...prev, [key]: !!saving }));
  };

  // Transient "Saved ✓" feedback per tenant row after a successful action
  const [savedByTenantId, setSavedByTenantId] = useState({});
  const isSavedTenant = (tenantId) => !!savedByTenantId?.[String(tenantId)];
  const flashSavedTenant = (tenantId) => {
    const key = String(tenantId || '');
    if (!key) return;
    setSavedByTenantId(prev => ({ ...prev, [key]: true }));
    setTimeout(() => {
      setSavedByTenantId(prev => ({ ...prev, [key]: false }));
    }, 2500);
  };

  // Transient "✓" feedback for one-shot panel buttons (revoke invites, etc.)
  const [flashedActions, setFlashedActions] = useState({});
  const flashAction = (key) => {
    setFlashedActions(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setFlashedActions(prev => ({ ...prev, [key]: false })), 2500);
  };

  // "Granted ✓" sticks until the day count is changed again (or access revoked)
  const [lastGrantedDays, setLastGrantedDays] = useState(null);
  useEffect(() => { setLastGrantedDays(null); }, [selectedTenant?._id]);
  useEffect(() => {
    if (!selectedTenant) {
      setSeatLimitDraft('');
      return;
    }
    setSeatLimitDraft(String(selectedTenant.seats || 1));
  }, [selectedTenant?._id, selectedTenant?.seats]);

  const applyTenantServerUpdate = (tenantId, updated, fallbackPatch) => {
    setTenants((prev) => mergeTenantUpdate(prev, tenantId, updated, fallbackPatch));
    setSelectedTenant((prev) => {
      if (!prev || tenantKey(prev) !== String(tenantId)) return prev;
      const merged = mergeTenantUpdate([prev], tenantId, updated, fallbackPatch)[0];
      return merged || prev;
    });
  };

  const handleSetPlan = async (tenant, planValue) => {
    const next = String(planValue || '').toLowerCase();
    if (!next) return;
    const tenantId = tenantKey(tenant);
    if (!tenantId) return;
    try {
      setError('');
      setSavingTenant(tenantId, true);
      const updated = await ApiService.updateTenantPlan?.(tenantId, { plan: next });
      applyTenantServerUpdate(tenantId, updated, { plan: next });
      flashSavedTenant(tenantId);
    } catch (e) {
      setError(e?.message || 'Failed to update plan');
    } finally {
      setSavingTenant(tenantId, false);
    }
  };

  // Inline tenant rename (Accounts table)
  const [renamingTenantId, setRenamingTenantId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const startRenameTenant = (tenant) => {
    setRenamingTenantId(tenantKey(tenant));
    setRenameValue(tenant?.name || '');
  };

  const cancelRenameTenant = () => {
    setRenamingTenantId(null);
    setRenameValue('');
  };

  const handleRenameTenant = async (tenant) => {
    const tenantId = tenantKey(tenant);
    const nextName = String(renameValue || '').trim().slice(0, 80);
    if (!tenantId) return;
    if (!nextName || nextName === (tenant?.name || '')) {
      cancelRenameTenant();
      return;
    }
    try {
      setError('');
      setSavingTenant(tenantId, true);
      const updated = await ApiService.updateTenant?.(tenantId, { name: nextName });
      applyTenantServerUpdate(tenantId, updated, { name: nextName });
      flashSavedTenant(tenantId);
      cancelRenameTenant();
    } catch (e) {
      setError(e?.message || 'Failed to rename tenant');
    } finally {
      setSavingTenant(tenantId, false);
    }
  };

  // Grant (or revoke with a negative number) prepaid AI credits
  const handleGrantAiCredits = async (tenant) => {
    const tenantId = tenantKey(tenant);
    if (!tenantId) return;
    const raw = window.prompt('Grant AI credits (negative to revoke):', '100');
    if (raw === null) return;
    const grant = parseInt(raw, 10);
    if (!Number.isFinite(grant) || grant === 0) return;
    try {
      setError('');
      setSavingTenant(tenantId, true);
      const res = await ApiService.grantTenantAiCredits(tenantId, grant);
      applyTenantServerUpdate(tenantId, null, {
        aiCredits: {
          ...(tenant.aiCredits || {}),
          purchased: res?.balance?.purchased ?? Math.max(0, (tenant.aiCredits?.purchased || 0) + grant),
          used: res?.balance?.used ?? tenant.aiCredits?.used ?? 0,
        }
      });
      flashSavedTenant(tenantId);
    } catch (e) {
      setError(e?.message || 'Failed to grant AI credits');
    } finally {
      setSavingTenant(tenantId, false);
    }
  };

  const persistTenantSeats = async (tenantId, nextSeats) => {
    if (!tenantId) throw new Error('Tenant id is required');
    const seats = Math.max(1, Math.min(10000, parseInt(nextSeats, 10) || 0));
    if (!Number.isFinite(seats) || seats < 1) {
      throw new Error('Seat limit must be a positive integer');
    }
    const updated = await ApiService.updateTenant(tenantId, { seats });
    const confirmed = Number(updated?.seats);
    if (!Number.isFinite(confirmed) || confirmed !== seats) {
      throw new Error(`Seat limit did not save (expected ${seats}, got ${updated?.seats ?? 'empty response'})`);
    }
    applyTenantServerUpdate(tenantId, updated, { seats });
    setSeatLimitDraft(String(confirmed));
    flashSavedTenant(tenantId);
    await fetchSeatUsage(tenantId);
    return confirmed;
  };

  const handleAdjustSeats = async (tenant, delta) => {
    const tenantId = tenantKey(tenant);
    if (!tenantId) return;
    const nextSeats = Math.max(1, (tenant.seats || 1) + delta);
    try {
      setError('');
      setSavingTenant(tenantId, true);
      await persistTenantSeats(tenantId, nextSeats);
    } catch (e) {
      setError(e?.message || 'Failed to adjust seats');
    } finally {
      setSavingTenant(tenantId, false);
    }
  };

  const handleSetSeatLimit = async () => {
    if (!selectedTenant?._id) return;
    const tenantId = tenantKey(selectedTenant);
    const nextSeats = Math.max(1, Math.min(10000, parseInt(seatLimitDraft, 10) || 0));
    if (!Number.isFinite(nextSeats) || nextSeats < 1) {
      setError('Seat limit must be a positive integer');
      return;
    }
    try {
      setError('');
      setBillingBusy(true);
      setSavingTenant(tenantId, true);
      await persistTenantSeats(tenantId, nextSeats);
    } catch (e) {
      setError(e?.message || 'Failed to update seat limit');
    } finally {
      setSavingTenant(tenantId, false);
      setBillingBusy(false);
    }
  };

  const handleGrantTemporaryAccess = async () => {
    if (!selectedTenant?._id) return;
    if (!ApiService.setTenantTemporaryAccess) {
      setError('Temporary access endpoint is not available.');
      return;
    }
    const days = Math.max(1, Math.min(90, parseInt(tempAccessDays || '7', 10) || 7));
    try {
      setBillingBusy(true);
      setError('');
      await ApiService.setTenantTemporaryAccess(selectedTenant._id, { days });
      await fetchTenants();
      // keep selected tenant fresh (fetchTenants may return a new object)
      setSelectedTenant(prev => prev ? { ...prev } : prev);
      await fetchSeatUsage(selectedTenant._id);
      setLastGrantedDays(days);
    } catch (e) {
      setError(e?.message || 'Failed to grant temporary access');
    } finally {
      setBillingBusy(false);
    }
  };

  const handleRevokeTemporaryAccess = async () => {
    if (!selectedTenant?._id) return;
    if (!ApiService.clearTenantTemporaryAccess) {
      setError('Temporary access endpoint is not available.');
      return;
    }
    try {
      setBillingBusy(true);
      setError('');
      await ApiService.clearTenantTemporaryAccess(selectedTenant._id);
      await fetchTenants();
      setSelectedTenant(prev => prev ? { ...prev } : prev);
      await fetchSeatUsage(selectedTenant._id);
      setLastGrantedDays(null);
      flashAction('revokeTemp');
    } catch (e) {
      setError(e?.message || 'Failed to revoke temporary access');
    } finally {
      setBillingBusy(false);
    }
  };

  const handleOpenBillingPortal = async () => {
    if (!selectedTenant?._id) return;
    if (!ApiService.createTenantBillingPortalLink) {
      setError('Billing portal endpoint is not available.');
      return;
    }
    try {
      setBillingBusy(true);
      setError('');
      const returnUrl = `${window.location.origin}/admin`;
      const resp = await ApiService.createTenantBillingPortalLink(selectedTenant._id, { returnUrl });
      const url = resp?.url;
      if (!url) throw new Error('No billing portal URL returned');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e?.message || 'Failed to open billing portal');
    } finally {
      setBillingBusy(false);
    }
  };

  // Seat enforcement tools
  const handleDeactivateUser = async (user) => {
    if (!selectedTenant?._id || !user?._id) return;
    const email = user.email || 'this user';
    const ok = window.confirm(
      `Deactivate ${email}?\n\n` +
      `This will mark the user as inactive and can free up a seat. ` +
      `They may lose access immediately.`
    );
    if (!ok) return;
    try {
      setError('');
      setBillingBusy(true);
      await ApiService.updateUser(user._id, { status: 'inactive' });
      await refreshSelectedTenantPanels(selectedTenant);
    } catch (e) {
      setError(e?.message || 'Failed to deactivate user');
    } finally {
      setBillingBusy(false);
    }
  };

  const handleBulkRevokeExpiredInvites = async () => {
    if (!selectedTenant?._id) return;
    const ok = window.confirm(
      `Revoke all expired invitations for this tenant?\n\n` +
      `This will mark expired invites as expired so they no longer count towards seat usage.`
    );
    if (!ok) return;
    if (!ApiService.bulkRevokeExpiredInvitations) {
      setError('Invitation revoke endpoint is not available.');
      return;
    }
    try {
      setError('');
      setBillingBusy(true);
      await ApiService.bulkRevokeExpiredInvitations();
      await fetchSeatUsage(selectedTenant._id);
      flashAction('revokeInvites');
    } catch (e) {
      setError(e?.message || 'Failed to revoke expired invites');
    } finally {
      setBillingBusy(false);
    }
  };

  // Curated feature flags (must match backend CURATED_TENANT_FLAGS)
  const CURATED_FLAGS = [
    { key: 'aiAssist', type: 'boolean', label: 'AI Assist' },
    { key: 'aiCompanion', type: 'boolean', label: 'AI Companion (chat assistant)' },
    { key: 'api', type: 'boolean', label: 'API Access' },
    { key: 'rateLimit', type: 'enum', label: 'Rate limiting', options: [
      { value: 'off', label: 'Off' },
      { value: 'standard', label: 'Standard' },
      { value: 'strict', label: 'Strict' }
    ]},
    { key: 'auditVerbose', type: 'boolean', label: 'Verbose audit logging' },
    { key: 'stripeIntegration', type: 'boolean', label: 'Stripe integration (self-upgrade)' },
    { key: 'twoFA', type: 'boolean', label: '2FA required' },
    { key: 'emailVerification', type: 'boolean', label: 'Email verification (unused — use Feature flags tab)' }
  ];

  const getTenantFeatureValue = (tenant, k) => {
    const f = tenant?.features || {};
    if (Object.prototype.hasOwnProperty.call(f, k)) return f[k];
    // client defaults (keep aligned with backend defaults)
    const defaults = { aiAssist: true, aiCompanion: true, api: true, rateLimit: 'standard', auditVerbose: false, stripeIntegration: true, twoFA: false, emailVerification: false };
    return defaults[k];
  };

  const updateTenantFeaturesCurated = async (tenant, patch) => {
    const tenantId = tenantKey(tenant);
    if (!tenantId) return;
    try {
      setError('');
      setSavingTenant(tenantId, true);
      // Send only curated keys as patch; server will merge/sanitize
      const updated = await ApiService.updateTenant?.(tenantId, { features: patch });
      applyTenantServerUpdate(
        tenantId,
        updated,
        { features: { ...(tenant.features || {}), ...patch } }
      );
    } catch (e) {
      setError(e?.message || 'Failed to update feature flags');
    } finally {
      setSavingTenant(tenantId, false);
    }
  };

  // --------------------
  // Branding & subdomain (co-branded <slug>.testera.io login)
  // --------------------
  const [brandingForm, setBrandingForm] = useState({ subdomainEnabled: false, displayName: '', logoUrl: '', accentColor: '' });
  const [brandingSaving, setBrandingSaving] = useState(false);

  useEffect(() => {
    const b = selectedTenant?.branding || {};
    setBrandingForm({
      subdomainEnabled: !!b.subdomainEnabled,
      displayName: b.displayName || '',
      logoUrl: b.logoUrl || '',
      accentColor: b.accentColor || '',
    });
  }, [selectedTenant]);

  // Dirty when the form differs from the tenant's saved branding; saving
  // refreshes selectedTenant, which re-syncs the form and clears this.
  const brandingDirty = (() => {
    const b = selectedTenant?.branding || {};
    return (
      (!!brandingForm.subdomainEnabled !== !!b.subdomainEnabled)
      || (brandingForm.displayName || '') !== (b.displayName || '')
      || (brandingForm.logoUrl || '') !== (b.logoUrl || '')
      || (brandingForm.accentColor || '') !== (b.accentColor || '')
    );
  })();

  const handleSaveBranding = async () => {
    const tenantId = tenantKey(selectedTenant);
    if (!tenantId) return;
    try {
      setError('');
      setBrandingSaving(true);
      const updated = await ApiService.updateTenant?.(tenantId, { branding: brandingForm });
      applyTenantServerUpdate(tenantId, updated, { branding: { ...brandingForm } });
    } catch (e) {
      setError(e?.message || 'Failed to save branding');
    } finally {
      setBrandingSaving(false);
    }
  };

  // --------------------
  // Ops / Observability
  // --------------------
  const [adminTab, setAdminTab] = useState('accounts'); // 'accounts' | 'ops' | 'audit' | 'visitors' | 'sessions' | 'flags' | 'plugins' | 'security'

  // Cross-tenant sessions
  const [adminSessions, setAdminSessions] = useState([]);
  const [adminSessionsActiveCount, setAdminSessionsActiveCount] = useState(0);
  const [adminSessionsLoading, setAdminSessionsLoading] = useState(false);
  const [adminSessionsError, setAdminSessionsError] = useState('');
  const [adminSessionsQ, setAdminSessionsQ] = useState('');
  const [adminSessionsTenantId, setAdminSessionsTenantId] = useState('');
  const [adminSessionsActiveOnly, setAdminSessionsActiveOnly] = useState(false);
  const [adminSessionRevokingId, setAdminSessionRevokingId] = useState(null);
  const [opsStatus, setOpsStatus] = useState(null);
  const [opsMetrics, setOpsMetrics] = useState(null);
  const [opsLoading, setOpsLoading] = useState(false);

  // Ops charts are derived entirely from the two payloads already fetched — no extra calls.
  const opsCharts = useMemo(() => {
    const routes = Array.isArray(opsMetrics?.routes) ? opsMetrics.routes : [];
    const byClass = opsMetrics?.byStatusClass || opsStatus?.metrics?.byStatusClass || {};
    const total = Number(opsMetrics?.totalRequests || opsStatus?.metrics?.totalRequests || 0);

    const classOrder = ['2xx', '3xx', '4xx', '5xx'];
    const statusSeries = classOrder
      .filter((k) => Number(byClass[k] || 0) > 0)
      .map((k) => ({ label: k, value: Number(byClass[k] || 0) }));
    for (const [k, v] of Object.entries(byClass)) {
      if (!classOrder.includes(k) && Number(v) > 0) statusSeries.push({ label: k, value: Number(v) });
    }

    const topTraffic = routes.slice(0, 8).map((r) => ({ label: shortRoute(r.route), value: r.count || 0 }));
    const slowest = routes
      .filter((r) => r.p95 != null && (r.count || 0) > 1)
      .sort((a, b) => (b.p95 || 0) - (a.p95 || 0))
      .slice(0, 8)
      .map((r) => ({ label: shortRoute(r.route), value: Math.round(r.p95) }));
    const failing = routes
      .filter((r) => (r.errors || 0) > 0)
      .sort((a, b) => (b.errorRate || 0) - (a.errorRate || 0))
      .slice(0, 8)
      .map((r) => ({ label: shortRoute(r.route), value: Math.round((r.errorRate || 0) * 1000) / 10 }));

    const errs = Number(byClass['5xx'] || 0);
    const client = Number(byClass['4xx'] || 0);
    const worstP95 = routes.reduce((mx, r) => Math.max(mx, Number(r.p95 || 0)), 0);
    return {
      total,
      routeCount: routes.length,
      errorPct: total ? (errs / total) * 100 : 0,
      clientPct: total ? (client / total) * 100 : 0,
      worstP95,
      statusSeries,
      topTraffic,
      slowest,
      failing,
    };
  }, [opsMetrics, opsStatus]);

  // --------------------
  // Audit drilldown
  // --------------------
  const [auditSearch, setAuditSearch] = useState({ q: '', userEmail: '', entityType: '', action: '', path: '', tenantId: '', tenantSlug: '', statusMin: '', statusMax: '', from: '', to: '', limit: 100, skip: 0, hideNoise: true });
  const [adminAudit, setAdminAudit] = useState({ total: 0, count: 0, logs: [] });
  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState(null);
  const [auditDiff, setAuditDiff] = useState(null);
  const [auditDiffLoading, setAuditDiffLoading] = useState(false);

  const fetchOps = async ({ quiet = false } = {}) => {
    if (!ApiService.getOpsStatus || !ApiService.getOpsMetrics) {
      if (!quiet) setError('Ops endpoints are not available.');
      return;
    }
    try {
      if (!quiet) setError('');
      if (!quiet) setOpsLoading(true);
      const [s, m] = await Promise.all([
        ApiService.getOpsStatus(),
        ApiService.getOpsMetrics({ top: 50 })
      ]);
      setOpsStatus(s);
      setOpsMetrics(m);
      setLastRefreshedAt(new Date());
    } catch (e) {
      if (!quiet) setError(e?.message || 'Failed to load ops status');
    } finally {
      if (!quiet) setOpsLoading(false);
    }
  };

  const submitAuditSearch = async (next = {}) => {
    if (!ApiService.searchAdminAuditLogs) {
      setError('Admin audit search endpoint is not available.');
      return;
    }
    try {
      setError('');
      setAuditLoading(true);
      const params = { ...auditSearch, ...next };
      const resp = await ApiService.searchAdminAuditLogs(params);
      setAdminAudit({ total: resp?.total || 0, count: resp?.count || 0, logs: resp?.logs || [] });
      setAuditSearch(params);
    } catch (e) {
      setError(e?.message || 'Failed to search audit logs');
    } finally {
      setAuditLoading(false);
    }
  };

  const openAuditDiff = async (log) => {
    setSelectedAuditLog(log);
    setAuditDiff(null);
    if (!log?._id || !ApiService.getAdminAuditDiff) return;
    try {
      setAuditDiffLoading(true);
      const d = await ApiService.getAdminAuditDiff(log._id);
      setAuditDiff(d);
    } catch (e) {
      setAuditDiff({ available: false, error: e?.message || 'Failed fetching diff' });
    } finally {
      setAuditDiffLoading(false);
    }
  };

  const fetchAdminSessions = async (overrides = {}) => {
    if (!ApiService.listAdminSessions) {
      setAdminSessionsError('Sessions endpoint is not available.');
      return;
    }
    const quiet = !!overrides.quiet;
    const params = {
      q: adminSessionsQ,
      tenantId: adminSessionsTenantId,
      active: adminSessionsActiveOnly ? '1' : undefined,
      limit: 300,
      ...overrides,
    };
    delete params.quiet;
    try {
      if (!quiet) setAdminSessionsLoading(true);
      if (!quiet) setAdminSessionsError('');
      const res = await ApiService.listAdminSessions(params);
      setAdminSessions(Array.isArray(res?.sessions) ? res.sessions : []);
      setAdminSessionsActiveCount(Number(res?.activeCount) || 0);
      setLastRefreshedAt(new Date());
    } catch (e) {
      if (!quiet) {
        setAdminSessionsError(e?.message || 'Failed to load sessions');
        setAdminSessions([]);
        setAdminSessionsActiveCount(0);
      }
    } finally {
      if (!quiet) setAdminSessionsLoading(false);
    }
  };

  const handleRevokeAdminSession = async (sessionId) => {
    if (!sessionId) return;
    if (!window.confirm('Revoke this session? The user will need to sign in again on that device.')) return;
    setAdminSessionRevokingId(sessionId);
    try {
      await ApiService.revokeAdminSession(sessionId);
      await fetchAdminSessions();
    } catch (e) {
      setAdminSessionsError(e?.message || 'Failed to revoke session');
    } finally {
      setAdminSessionRevokingId(null);
    }
  };

  // Load ops/audit/sessions data when switching tabs
  useEffect(() => {
    if (!isAuthenticated || !isSuperAdmin) return;
    if (adminTab === 'ops') fetchOps();
    if (adminTab === 'audit') submitAuditSearch({ skip: 0 });
    if (adminTab === 'sessions') fetchAdminSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTab, isAuthenticated, isSuperAdmin]);

  useEffect(() => {
    if (isAuthenticated && isSuperAdmin) {
      if (!signupSinceRef.current) {
        // Small lookback so a signup that races the first poll is still caught.
        signupSinceRef.current = new Date(Date.now() - 60_000).toISOString();
      }
      fetchTenants();
    }
  }, [isAuthenticated, isSuperAdmin]);

  const pollRecentSignups = useCallback(async () => {
    if (!ApiService.getAdminRecentSignups) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    if (!signupSinceRef.current) {
      signupSinceRef.current = new Date(Date.now() - 60_000).toISOString();
    }
    try {
      const resp = await ApiService.getAdminRecentSignups({
        since: signupSinceRef.current,
        limit: 20,
      });
      const list = Array.isArray(resp?.signups) ? resp.signups : [];
      if (list.length) {
        celebrateSignups(list);
        const latest = list.reduce((max, s) => {
          const t = new Date(s.at || 0).getTime();
          return t > max ? t : max;
        }, new Date(signupSinceRef.current).getTime());
        // Advance watermark by 1ms so the same row is not re-fetched forever.
        signupSinceRef.current = new Date(latest + 1).toISOString();
      }
    } catch (e) {
      // Surface once in console — silent banner would hide deploy/auth issues.
      console.warn('[admin] signup poll failed', e?.message || e);
    }
  }, [celebrateSignups]);

  const quietRefresh = useCallback(async () => {
    if (refreshInFlightRef.current) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    refreshInFlightRef.current = true;
    try {
      await fetchTenants({ quiet: true });
      await pollRecentSignups();
      setRefreshTick((n) => n + 1);
      if (adminTab === 'ops') await fetchOps({ quiet: true });
      if (adminTab === 'sessions' && ApiService.listAdminSessions) {
        try { await fetchAdminSessions({ quiet: true }); } catch { /* quiet */ }
      }
    } finally {
      refreshInFlightRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTab, pollRecentSignups]);

  // Fast signup watch (works on every tab, including Visitors).
  useEffect(() => {
    if (!isAuthenticated || !isSuperAdmin || !autoRefresh) return undefined;
    pollRecentSignups();
    const id = setInterval(() => { pollRecentSignups(); }, ADMIN_SIGNUP_POLL_MS);
    const onVis = () => {
      if (!document.hidden) pollRecentSignups();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isAuthenticated, isSuperAdmin, autoRefresh, pollRecentSignups]);

  useEffect(() => {
    if (!isAuthenticated || !isSuperAdmin || !autoRefresh) return undefined;
    const id = setInterval(() => { quietRefresh(); }, ADMIN_AUTO_REFRESH_MS);
    const onVis = () => {
      if (!document.hidden) quietRefresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isAuthenticated, isSuperAdmin, autoRefresh, quietRefresh]);

  const startMfaChallenge = async (token, setupRequired) => {
    setMfaError('');
    setMfaCode('');
    setMfaToken(token);
    if (setupRequired) {
      try {
        setMfaLoading(true);
        const resp = await ApiService.setup2FA(token);
        setMfaQr(resp?.qrDataUrl || '');
        setMfaSecret(resp?.secret || '');
        setMfaStep('setup');
      } catch (e) {
        setError(e?.message || 'Failed to start 2FA setup. Please sign in again.');
        setMfaStep(null);
      } finally {
        setMfaLoading(false);
      }
    } else {
      setMfaStep('code');
    }
  };

  const completeAdminLogin = (user) => {
    const allowed = !!(user?.isSuperAdmin || String(user?.email || '').toLowerCase() === 'admin@testera.io');
    if (!allowed) {
      setError('Superadmin credentials required (tenant Admin accounts cannot use this portal)');
      try { logout?.(); } catch {}
      return false;
    }
    try {
      if (user?.token) ApiService.setToken(user.token);
      localStorage.setItem('testara_currentUser', JSON.stringify(user));
    } catch { /* ignore */ }
    setCurrentUser?.(user);
    setIsAuthenticated?.(true);
    setMfaStep(null);
    setMfaToken('');
    return true;
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError('');
      const ok = await login(email, password);
      if (ok?.mfaRequired) {
        await startMfaChallenge(ok.mfaToken, !!ok.mfaSetupRequired);
        return;
      }
      if (!ok || !completeAdminLogin(ok)) {
        if (!ok?.mfaRequired) {
          setError('Superadmin credentials required (tenant Admin accounts cannot use this portal)');
          try { logout?.(); } catch {}
        }
      }
    } catch (e) {
      setError(e?.message || 'Login failed — server unreachable');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async (e) => {
    e?.preventDefault?.();
    setMfaError('');
    const code = mfaCode.replace(/\s+/g, '');
    if (!/^\d{6}$/.test(code)) {
      setMfaError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setMfaLoading(true);
    try {
      const user = await ApiService.verify2FA({ mfaToken, code });
      if (!completeAdminLogin(user)) {
        setMfaError('This account is not a superadmin.');
      }
    } catch (err) {
      if (err?.code === 'mfa_session_invalid') {
        setError('Your 2FA session expired. Please sign in again.');
        setMfaStep(null);
      } else {
        setMfaError(err?.message || 'Verification failed. Try again.');
      }
    } finally {
      setMfaLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPwdMsg('');
    if (!pwdCurrent || !pwdNew) {
      setPwdMsg('Enter current and new password.');
      return;
    }
    if (pwdNew.length < 10) {
      setPwdMsg('New password must be at least 10 characters.');
      return;
    }
    if (pwdNew !== pwdNew2) {
      setPwdMsg('New passwords do not match.');
      return;
    }
    setPwdBusy(true);
    try {
      await ApiService.changePassword({ currentPassword: pwdCurrent, newPassword: pwdNew });
      setPwdCurrent('');
      setPwdNew('');
      setPwdNew2('');
      setPwdMsg('Password updated.');
    } catch (e) {
      setPwdMsg(e?.message || 'Failed to change password');
    } finally {
      setPwdBusy(false);
    }
  };

  const handleStartSecurity2fa = async () => {
    setSec2faMsg('');
    setSec2faBusy(true);
    try {
      const resp = await ApiService.setup2FA(); // authenticated session (no mfaToken)
      setSec2faQr(resp?.qrDataUrl || '');
      setSec2faSecret(resp?.secret || '');
      setSec2faMsg('Scan the QR code, then enter a 6-digit code to activate.');
    } catch (e) {
      setSec2faMsg(e?.message || 'Failed to start 2FA setup');
    } finally {
      setSec2faBusy(false);
    }
  };

  const handleConfirmSecurity2fa = async () => {
    setSec2faMsg('');
    const code = sec2faCode.replace(/\s+/g, '');
    if (!/^\d{6}$/.test(code)) {
      setSec2faMsg('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setSec2faBusy(true);
    try {
      await ApiService.verify2FA({ code }); // authenticated enrollment
      setSec2faQr('');
      setSec2faSecret('');
      setSec2faCode('');
      setSec2faMsg('Two-factor authentication is now enabled.');
      try {
        const me = await ApiService.getMe();
        const merged = { ...currentUser, ...me, twoFactorEnabled: true };
        setCurrentUser?.(merged);
        localStorage.setItem('testara_currentUser', JSON.stringify(merged));
      } catch { /* ignore */ }
    } catch (e) {
      setSec2faMsg(e?.message || 'Failed to verify code');
    } finally {
      setSec2faBusy(false);
    }
  };

  if (!isAuthenticated || !isSuperAdmin) {
    return (
      <div className="dark min-h-screen flex items-center justify-center bg-[#050807] text-slate-200">
        <div className="w-full max-w-md app-glass p-8 shadow-lg shadow-black/30">
          <h1 className="text-xl font-semibold mb-2">Admin Login</h1>
          <p className="text-xs text-slate-400 mb-5">
            {mfaStep
              ? 'Complete two-factor authentication to continue.'
              : 'Sign in with the Testera superadmin account. 2FA is required.'}
          </p>
          {error && <div className="text-xs mb-3 text-amber-300">{error}</div>}

          {mfaStep ? (
            <form onSubmit={handleVerifyMfa} className="space-y-3">
              {mfaStep === 'setup' && (
                <div className="space-y-2 text-xs text-slate-400">
                  <p>Scan this QR with Google Authenticator / 1Password / Authy, then enter a code.</p>
                  {mfaQr && (
                    <div className="flex justify-center">
                      <img src={mfaQr} alt="2FA QR code" className="rounded-lg border border-white/15 bg-white p-1" />
                    </div>
                  )}
                  {mfaSecret && (
                    <p className="break-all">
                      Manual key: <span className="font-mono text-slate-300">{mfaSecret}</span>
                    </p>
                  )}
                </div>
              )}
              {mfaError && <div className="text-xs text-amber-300">{mfaError}</div>}
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="6-digit code"
                className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/15 text-slate-200 focus:outline-none focus:border-teal-500"
              />
              <button
                type="submit"
                disabled={mfaLoading}
                className="w-full px-4 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-medium disabled:opacity-60"
              >
                {mfaLoading ? 'Verifying…' : mfaStep === 'setup' ? 'Activate & Sign In' : 'Verify & Sign In'}
              </button>
              <button
                type="button"
                onClick={() => { setMfaStep(null); setMfaToken(''); setError(''); }}
                className="w-full text-xs text-slate-500 hover:text-slate-300"
              >
                Back to password
              </button>
            </form>
          ) : (
            <>
              <div className="space-y-3 mb-4">
                <input
                  type="text"
                  value={email}
                  onChange={e=>setEmail(e.target.value)}
                  placeholder="Email or username"
                  className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/15 text-slate-200 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-primary"
                />
                <input
                  type="password"
                  value={password}
                  onChange={e=>setPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
                  placeholder="Password"
                  className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/15 text-slate-200 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-primary"
                />
              </div>
              <button
                disabled={loading}
                onClick={handleLogin}
                className="w-full px-4 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >{loading ? 'Signing in…' : 'Sign In'}</button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dark min-h-screen app-shell-bg text-slate-200 selection:bg-teal-500/30 selection:text-white">
      <div className="max-w-screen-2xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-semibold tracking-tight">Admin Portal</h1>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setAdminTab('accounts')}
                className={`text-sm px-3 py-2 rounded-lg border ${adminTab === 'accounts' ? 'bg-teal-500/15 border-teal-500/40 text-teal-200' : 'bg-surface-1 border-white/10 text-slate-300 hover:border-white/15'}`}
              >Accounts</button>
              <button
                onClick={() => setAdminTab('ops')}
                className={`text-sm px-3 py-2 rounded-lg border ${adminTab === 'ops' ? 'bg-teal-500/15 border-teal-500/40 text-teal-200' : 'bg-surface-1 border-white/10 text-slate-300 hover:border-white/15'}`}
              >Ops</button>
              <button
                onClick={() => setAdminTab('audit')}
                className={`text-sm px-3 py-2 rounded-lg border ${adminTab === 'audit' ? 'bg-teal-500/15 border-teal-500/40 text-teal-200' : 'bg-surface-1 border-white/10 text-slate-300 hover:border-white/15'}`}
              >Audit</button>
              <button
                onClick={() => setAdminTab('visitors')}
                className={`text-sm px-3 py-2 rounded-lg border ${adminTab === 'visitors' ? 'bg-teal-500/15 border-teal-500/40 text-teal-200' : 'bg-surface-1 border-white/10 text-slate-300 hover:border-white/15'}`}
              >Visitors</button>
              <button
                onClick={() => setAdminTab('sessions')}
                className={`text-sm px-3 py-2 rounded-lg border ${adminTab === 'sessions' ? 'bg-teal-500/15 border-teal-500/40 text-teal-200' : 'bg-surface-1 border-white/10 text-slate-300 hover:border-white/15'}`}
              >Sessions</button>
              <button
                onClick={() => setAdminTab('flags')}
                className={`text-sm px-3 py-2 rounded-lg border ${adminTab === 'flags' ? 'bg-teal-500/15 border-teal-500/40 text-teal-200' : 'bg-surface-1 border-white/10 text-slate-300 hover:border-white/15'}`}
              >Feature flags</button>
              <button
                onClick={() => setAdminTab('ai')}
                className={`text-sm px-3 py-2 rounded-lg border ${adminTab === 'ai' ? 'bg-teal-500/15 border-teal-500/40 text-teal-200' : 'bg-surface-1 border-white/10 text-slate-300 hover:border-white/15'}`}
              >AI Provider</button>
              <button
                onClick={() => setAdminTab('plugins')}
                className={`text-sm px-3 py-2 rounded-lg border ${adminTab === 'plugins' ? 'bg-teal-500/15 border-teal-500/40 text-teal-200' : 'bg-surface-1 border-white/10 text-slate-300 hover:border-white/15'}`}
              >Marketplace</button>
              <button
                onClick={() => setAdminTab('security')}
                className={`text-sm px-3 py-2 rounded-lg border ${adminTab === 'security' ? 'bg-teal-500/15 border-teal-500/40 text-teal-200' : 'bg-surface-1 border-white/10 text-slate-300 hover:border-white/15'}`}
              >Security</button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/10 bg-white/[0.04] text-[11px] text-slate-400 cursor-pointer select-none" title="Watch for new signups every 8s; refresh accounts/ops every 30s">
              <input
                type="checkbox"
                className="accent-teal-500"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${autoRefresh ? 'bg-primary animate-pulse' : 'bg-slate-600'}`} />
              Live
              {lastRefreshedAt ? (
                <span className="text-slate-600 tabular-nums">
                  {lastRefreshedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              ) : null}
            </label>
            <button
              type="button"
              onClick={() => setShowCompanion(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-500/15 border border-teal-500/40 text-teal-200 hover:bg-teal-400/30 text-sm"
            >
              <Sparkles className="w-3.5 h-3.5" />
              AI Companion
            </button>
            <button onClick={logout} className="px-3 py-2 rounded-lg bg-white/5 border border-white/15 hover:border-white/20 text-sm">Logout</button>
          </div>
        </div>

        {error && <div className="mb-4 text-xs text-amber-300">{error}</div>}

        <SignupFireworks active={fireworksActive} onDone={() => setFireworksActive(false)} />

        {adminTab === 'visitors' && <AdminVisitorsTab refreshTick={refreshTick} />}

        {adminTab === 'flags' && <AdminFeatureFlagsTab refreshTick={refreshTick} />}

        {adminTab === 'ai' && <AdminAiProviderTab refreshTick={refreshTick} />}

        {adminTab === 'plugins' && <AdminPluginsTab tenants={tenants} refreshTick={refreshTick} />}

        {adminTab === 'sessions' && (
          <div className="space-y-4">
            <div className="app-panel p-4">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-sm font-semibold">Active sessions</h2>
                  <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
                    Browser sessions across all tenants (last 7 days). “Online” means activity within the last 15 minutes. Revoking ends that device’s JWT immediately.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2 py-1 rounded text-[11px] border border-white/15 bg-white/5 text-slate-300">
                    Online now: {adminSessionsActiveCount}
                  </span>
                  <button
                    type="button"
                    onClick={() => fetchAdminSessions()}
                    className="text-xs px-3 py-1.5 rounded border border-white/15 bg-white/5 text-slate-300 hover:border-white/20"
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-2 mb-4">
                <input
                  type="search"
                  value={adminSessionsQ}
                  onChange={(e) => setAdminSessionsQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchAdminSessions(); }}
                  placeholder="Search name, email, IP, surface…"
                  className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-sm text-slate-200 focus:outline-none focus:border-teal-500"
                />
                <select
                  value={adminSessionsTenantId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAdminSessionsTenantId(v);
                    fetchAdminSessions({ tenantId: v });
                  }}
                  className="px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-sm text-slate-200 focus:outline-none focus:border-teal-500 md:w-56"
                >
                  <option value="">All tenants</option>
                  {tenants.map((t) => (
                    <option key={tenantKey(t)} value={tenantKey(t)}>
                      {t.name || t.slug || tenantKey(t)}
                    </option>
                  ))}
                </select>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-xs text-slate-300 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={adminSessionsActiveOnly}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setAdminSessionsActiveOnly(v);
                      fetchAdminSessions({ active: v ? '1' : undefined });
                    }}
                  />
                  Online only
                </label>
                <button
                  type="button"
                  onClick={() => fetchAdminSessions()}
                  className="px-3 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium"
                >
                  Search
                </button>
              </div>

              {adminSessionsLoading && <div className="text-xs text-slate-500 mb-2">Loading sessions…</div>}
              {adminSessionsError && (
                <div className="text-xs text-amber-300 mb-2">{adminSessionsError}</div>
              )}

              <div className="overflow-auto border border-white/10 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-white/[0.04] text-slate-400">
                    <tr>
                      {['Tenant', 'User', 'Device', 'Last surface', 'Last seen', 'Status', ''].map((h) => (
                        <th key={h || 'actions'} className="text-left font-medium px-4 py-2 text-xs tracking-wide uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {adminSessions.map((s) => (
                      <tr key={s.id} className="hover:bg-white/[0.04]">
                        <td className="px-4 py-2">
                          <div className="font-medium text-slate-200">{s.tenantName || '—'}</div>
                          <div className="text-[11px] text-slate-500">{s.tenantSlug || s.tenantId || 'no tenant'}</div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="font-medium text-slate-200">{s.userName || '—'}</div>
                          <div className="text-[11px] text-slate-500">{s.userEmail}</div>
                          {s.current && <span className="text-[10px] uppercase tracking-wide text-teal-400">This device</span>}
                        </td>
                        <td className="px-4 py-2 text-slate-400 text-xs">
                          <div>{s.device || 'Unknown'}</div>
                          {s.ip ? <div className="text-[10px] opacity-70">{s.ip}</div> : null}
                          {s.loginMethod ? <div className="text-[10px] opacity-70">{s.loginMethod}</div> : null}
                        </td>
                        <td className="px-4 py-2 text-slate-400 text-xs">{s.lastSurface || '—'}</td>
                        <td className="px-4 py-2 text-slate-400 text-xs whitespace-nowrap">
                          {s.lastSeenAt ? new Date(s.lastSeenAt).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-0.5 text-[11px] rounded-full border ${s.active ? 'bg-teal-500/15 text-teal-300 border-teal-500/30' : 'bg-white/5 text-slate-400 border-white/15'}`}>
                            {s.active ? 'Online' : 'Idle'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          {!s.current && (
                            <button
                              type="button"
                              disabled={adminSessionRevokingId === s.id}
                              onClick={() => handleRevokeAdminSession(s.id)}
                              className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                            >
                              {adminSessionRevokingId === s.id ? 'Revoking…' : 'Revoke'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {!adminSessionsLoading && adminSessions.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-slate-500 text-sm">
                          No sessions recorded yet. They appear after users open the app.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {adminTab === 'security' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="app-panel p-4 space-y-3">
              <div>
                <h2 className="text-sm font-semibold">Change password</h2>
                <p className="text-xs text-slate-500 mt-0.5">Use a long passphrase (10+ characters).</p>
              </div>
              {pwdMsg && (
                <div className={`text-xs ${pwdMsg.includes('updated') ? 'text-teal-300' : 'text-amber-300'}`}>{pwdMsg}</div>
              )}
              <input
                type="password"
                value={pwdCurrent}
                onChange={(e) => setPwdCurrent(e.target.value)}
                placeholder="Current password"
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-sm"
              />
              <input
                type="password"
                value={pwdNew}
                onChange={(e) => setPwdNew(e.target.value)}
                placeholder="New password"
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-sm"
              />
              <input
                type="password"
                value={pwdNew2}
                onChange={(e) => setPwdNew2(e.target.value)}
                placeholder="Confirm new password"
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-sm"
              />
              <button
                type="button"
                disabled={pwdBusy}
                onClick={handleChangePassword}
                className="px-3 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm disabled:opacity-60"
              >
                {pwdBusy ? 'Saving…' : 'Update password'}
              </button>
            </div>

            <div className="app-panel p-4 space-y-3">
              <div>
                <h2 className="text-sm font-semibold">Two-factor authentication</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Required for superadmin login. Status:{' '}
                  <span className={currentUser?.twoFactorEnabled ? 'text-teal-300' : 'text-amber-300'}>
                    {currentUser?.twoFactorEnabled ? 'Enabled' : 'Not enabled'}
                  </span>
                </p>
              </div>
              {sec2faMsg && (
                <div className={`text-xs ${sec2faMsg.includes('enabled') || sec2faMsg.includes('Scan') ? 'text-teal-300' : 'text-amber-300'}`}>
                  {sec2faMsg}
                </div>
              )}
              {!currentUser?.twoFactorEnabled && !sec2faQr && (
                <button
                  type="button"
                  disabled={sec2faBusy}
                  onClick={handleStartSecurity2fa}
                  className="px-3 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm disabled:opacity-60"
                >
                  {sec2faBusy ? 'Starting…' : 'Set up authenticator'}
                </button>
              )}
              {sec2faQr && (
                <div className="space-y-3">
                  <div className="flex justify-center">
                    <img src={sec2faQr} alt="2FA QR code" className="rounded-lg border border-white/15 bg-white p-1" />
                  </div>
                  {sec2faSecret && (
                    <p className="text-xs text-slate-400 break-all">
                      Manual key: <span className="font-mono text-slate-300">{sec2faSecret}</span>
                    </p>
                  )}
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={sec2faCode}
                    onChange={(e) => setSec2faCode(e.target.value)}
                    placeholder="6-digit code"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-sm"
                  />
                  <button
                    type="button"
                    disabled={sec2faBusy}
                    onClick={handleConfirmSecurity2fa}
                    className="px-3 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm disabled:opacity-60"
                  >
                    {sec2faBusy ? 'Verifying…' : 'Activate 2FA'}
                  </button>
                </div>
              )}
              {currentUser?.twoFactorEnabled && (
                <p className="text-xs text-slate-400">
                  Authenticator is active. You will be prompted for a code on every admin login.
                </p>
              )}
            </div>
          </div>
        )}

        {adminTab === 'ops' && (
          <div className="space-y-4">
            <div className="app-panel p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">System status</h2>
                <button
                  disabled={opsLoading}
                  onClick={fetchOps}
                  className="text-xs px-2 py-1 rounded bg-white/5 border border-white/15"
                >{opsLoading ? 'Refreshing…' : 'Refresh'}</button>
              </div>

              {!opsStatus ? (
                <div className="text-xs text-slate-500 mt-3">{opsLoading ? 'Loading…' : 'No data yet.'}</div>
              ) : (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  <div className="p-3 rounded border border-white/10 bg-white/[0.04]">
                    <div className="text-slate-400">DB</div>
                    <div className="text-slate-200">{opsStatus?.db?.state || '—'}</div>
                    <div className="text-slate-400">connected: {String(opsStatus?.db?.connected)}</div>
                    <div className="text-slate-400">connecting: {String(opsStatus?.db?.connecting)}</div>
                  </div>
                  <div className="p-3 rounded border border-white/10 bg-white/[0.04]">
                    <div className="text-slate-400">Service</div>
                    <div className="text-slate-200">{opsStatus?.service || '—'}</div>
                    <div className="text-slate-400">uptime: {Math.round(Number(opsStatus?.uptimeSec || 0))}s</div>
                    <div className="text-slate-400">started: {opsStatus?.startedAt ? new Date(opsStatus.startedAt).toLocaleString() : '—'}</div>
                  </div>
                  <div className="p-3 rounded border border-white/10 bg-white/[0.04]">
                    <div className="text-slate-400">Build</div>
                    <div className="text-slate-200">env: {opsStatus?.build?.env || '—'}</div>
                    <div className="text-slate-400">version: {opsStatus?.build?.version || '—'}</div>
                    <div className="text-slate-400" title={opsStatus?.build?.commit || undefined}>
                      commit: {opsStatus?.build?.commit
                        ? (String(opsStatus.build.commit).length > 12
                          ? `${String(opsStatus.build.commit).slice(0, 7)}`
                          : opsStatus.build.commit)
                        : '—'}
                    </div>
                  </div>
                </div>
              )}

              {opsStatus?.metrics && (
                <div className="mt-4 text-xs">
                  <div className="text-slate-400 mb-2">Status classes</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(opsStatus.metrics.byStatusClass || {}).map(([k, v]) => (
                      <span key={k} className="px-2 py-1 rounded border border-white/10 bg-white/[0.04]">{k}: {v}</span>
                    ))}
                  </div>

                  {!!(opsStatus.metrics.lastErrors || []).length && (
                    <div className="mt-3">
                      <div className="text-slate-400 mb-1">Recent 5xx</div>
                      <div className="max-h-44 overflow-x-auto overflow-y-auto rounded border border-white/10">
                        <table className="w-full text-xs min-w-[640px]">
                          <thead className="text-slate-400 sticky top-0 bg-surface-1">
                            <tr className="text-left">
                              <th className="px-2 py-1 whitespace-nowrap">Time</th>
                              <th className="px-2 py-1 whitespace-nowrap">Method</th>
                              <th className="px-2 py-1 whitespace-nowrap">Path</th>
                              <th className="px-2 py-1 whitespace-nowrap">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {opsStatus.metrics.lastErrors.map((e, idx) => (
                              <tr key={idx} className="border-t border-white/10">
                                <td className="px-2 py-1 whitespace-nowrap">{e.ts ? new Date(e.ts).toLocaleString() : '—'}</td>
                                <td className="px-2 py-1 whitespace-nowrap">{e.method}</td>
                                <td className="px-2 py-1 font-mono text-[11px] whitespace-nowrap truncate max-w-[360px]" title={e.path || ''}>{e.path}</td>
                                <td className="px-2 py-1 whitespace-nowrap">{e.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="app-panel p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">Request metrics</h2>
                <button
                  disabled={opsLoading}
                  onClick={fetchOps}
                  className="text-xs px-2 py-1 rounded bg-white/5 border border-white/15"
                >Refresh</button>
              </div>

              {!opsMetrics ? (
                <div className="text-xs text-slate-500 mt-3">{opsLoading ? 'Loading…' : 'No data yet.'}</div>
              ) : (
                <>
                  <p className="mt-1 text-[11px] text-slate-500">
                    In-memory counters from the API instance that served this request — they reset on deploy
                    and, when several tasks are running, cover only the one you reached.
                    Window: {formatUptime(opsStatus?.uptimeSec)} since {opsMetrics.startedAt ? new Date(opsMetrics.startedAt).toLocaleString() : '—'}.
                  </p>

                  <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3">
                    <OpsStat label="Requests" value={opsCharts.total.toLocaleString()} hint={`${opsCharts.routeCount} routes`} />
                    <OpsStat
                      label="5xx rate"
                      value={`${opsCharts.errorPct.toFixed(2)}%`}
                      tone={opsCharts.errorPct >= 1 ? 'bad' : opsCharts.errorPct > 0 ? 'warn' : 'good'}
                      hint={`${(opsStatus?.metrics?.byStatusClass?.['5xx'] || 0).toLocaleString()} responses`}
                    />
                    <OpsStat
                      label="4xx rate"
                      value={`${opsCharts.clientPct.toFixed(1)}%`}
                      tone={opsCharts.clientPct >= 25 ? 'warn' : 'default'}
                    />
                    <OpsStat
                      label="Worst p95"
                      value={opsCharts.worstP95 ? `${Math.round(opsCharts.worstP95)}ms` : '—'}
                      tone={opsCharts.worstP95 >= 2000 ? 'bad' : opsCharts.worstP95 >= 800 ? 'warn' : 'good'}
                    />
                    <OpsStat label="Uptime" value={formatUptime(opsStatus?.uptimeSec)} hint={opsStatus?.build?.env || ''} />
                  </div>

                  <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {opsCharts.statusSeries.length > 0 && (
                      <ArtifactViz
                        compact
                        data={{
                          chartType: 'donut',
                          title: 'Responses by status class',
                          subtitle: 'All requests since this instance started',
                          series: opsCharts.statusSeries,
                        }}
                      />
                    )}
                    {opsCharts.topTraffic.length > 0 && (
                      <ArtifactViz
                        compact
                        data={{
                          chartType: 'horizontal_bar',
                          title: 'Busiest routes',
                          subtitle: 'Request count',
                          series: opsCharts.topTraffic,
                        }}
                      />
                    )}
                    {opsCharts.slowest.length > 0 && (
                      <ArtifactViz
                        compact
                        data={{
                          chartType: 'horizontal_bar',
                          title: 'Slowest routes (p95)',
                          subtitle: 'Milliseconds — routes hit more than once',
                          series: opsCharts.slowest,
                        }}
                      />
                    )}
                    {opsCharts.failing.length > 0 && (
                      <ArtifactViz
                        compact
                        data={{
                          chartType: 'horizontal_bar',
                          title: 'Highest 5xx rate',
                          subtitle: 'Percent of requests failing',
                          series: opsCharts.failing,
                        }}
                      />
                    )}
                  </div>

                  <div className="mt-3 overflow-x-auto rounded border border-white/10">
                    <table className="w-full text-xs min-w-[860px]">
                      <thead className="text-slate-400 sticky top-0 bg-surface-1">
                        <tr className="text-left">
                          <th className="px-2 py-1 whitespace-nowrap">Route</th>
                          <th className="px-2 py-1 whitespace-nowrap">Count</th>
                          <th className="px-2 py-1 whitespace-nowrap">Err%</th>
                          <th className="px-2 py-1 whitespace-nowrap">p50</th>
                          <th className="px-2 py-1 whitespace-nowrap">p90</th>
                          <th className="px-2 py-1 whitespace-nowrap">p95</th>
                          <th className="px-2 py-1 whitespace-nowrap">p99</th>
                          <th className="px-2 py-1 whitespace-nowrap">max</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(opsMetrics.routes || []).map(r => (
                          <tr key={r.route} className="border-t border-white/10">
                            <td className="px-2 py-1 font-mono text-[11px] whitespace-nowrap truncate max-w-[420px]" title={r.route || ''}>{r.route}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{r.count}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{(Number(r.errorRate || 0) * 100).toFixed(1)}%</td>
                            <td className="px-2 py-1 whitespace-nowrap">{r.p50 != null ? `${Math.round(r.p50)}ms` : '—'}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{r.p90 != null ? `${Math.round(r.p90)}ms` : '—'}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{r.p95 != null ? `${Math.round(r.p95)}ms` : '—'}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{r.p99 != null ? `${Math.round(r.p99)}ms` : '—'}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{r.max != null ? `${Math.round(r.max)}ms` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {adminTab === 'audit' && (
          <div className={`grid grid-cols-1 gap-4 ${selectedAuditLog ? 'xl:grid-cols-3' : ''}`}>
            {/* min-w-0 lets the table scroll inside the grid column instead of clipping */}
            <div className={`${selectedAuditLog ? 'xl:col-span-2' : ''} min-w-0 app-panel p-4`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold">Audit drilldown</h2>
                <button
                  disabled={auditLoading}
                  onClick={() => submitAuditSearch({ skip: 0 })}
                  className="text-xs px-2 py-1 rounded bg-white/5 border border-white/15"
                >{auditLoading ? 'Searching…' : 'Search'}</button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 text-xs">
                <input
                  value={auditSearch.q}
                  onChange={(e) => setAuditSearch(s => ({ ...s, q: e.target.value }))}
                  placeholder="q (path/action/user)"
                  className="px-2 py-2 rounded bg-white/5 border border-white/15"
                />
                <input
                  value={auditSearch.userEmail}
                  onChange={(e) => setAuditSearch(s => ({ ...s, userEmail: e.target.value }))}
                  placeholder="userEmail"
                  className="px-2 py-2 rounded bg-white/5 border border-white/15"
                />
                <input
                  value={auditSearch.entityType}
                  onChange={(e) => setAuditSearch(s => ({ ...s, entityType: e.target.value }))}
                  placeholder="entityType (projects/testcases/...)"
                  className="px-2 py-2 rounded bg-white/5 border border-white/15"
                />
                <input
                  value={auditSearch.action}
                  onChange={(e) => setAuditSearch(s => ({ ...s, action: e.target.value }))}
                  placeholder="action"
                  className="px-2 py-2 rounded bg-white/5 border border-white/15"
                />
                <input
                  value={auditSearch.path}
                  onChange={(e) => setAuditSearch(s => ({ ...s, path: e.target.value }))}
                  placeholder="path"
                  className="px-2 py-2 rounded bg-white/5 border border-white/15"
                />
                <input
                  value={auditSearch.tenantId || ''}
                  onChange={(e) => setAuditSearch(s => ({ ...s, tenantId: e.target.value }))}
                  placeholder="tenantId"
                  className="px-2 py-2 rounded bg-white/5 border border-white/15"
                />
                <input
                  value={auditSearch.tenantSlug || ''}
                  onChange={(e) => setAuditSearch(s => ({ ...s, tenantSlug: e.target.value }))}
                  placeholder="tenantSlug"
                  className="px-2 py-2 rounded bg-white/5 border border-white/15"
                />
                <div className="flex gap-2">
                  <input
                    value={auditSearch.statusMin}
                    onChange={(e) => setAuditSearch(s => ({ ...s, statusMin: e.target.value }))}
                    placeholder="statusMin"
                    className="w-1/2 px-2 py-2 rounded bg-white/5 border border-white/15"
                  />
                  <input
                    value={auditSearch.statusMax}
                    onChange={(e) => setAuditSearch(s => ({ ...s, statusMax: e.target.value }))}
                    placeholder="statusMax"
                    className="w-1/2 px-2 py-2 rounded bg-white/5 border border-white/15"
                  />
                </div>
                <input
                  type="date"
                  value={auditSearch.from}
                  onChange={(e) => setAuditSearch(s => ({ ...s, from: e.target.value }))}
                  className="px-2 py-2 rounded bg-white/5 border border-white/15"
                />
                <input
                  type="date"
                  value={auditSearch.to}
                  onChange={(e) => setAuditSearch(s => ({ ...s, to: e.target.value }))}
                  className="px-2 py-2 rounded bg-white/5 border border-white/15"
                />
                <select
                  value={auditSearch.limit}
                  onChange={(e) => setAuditSearch(s => ({ ...s, limit: parseInt(e.target.value, 10), skip: 0 }))}
                  className="px-2 py-2 rounded bg-white/5 border border-white/15"
                >
                  {[50, 100, 200, 500].map(n => <option key={n} value={n}>{n}/page</option>)}
                </select>
                <label
                  className="flex items-center gap-2 px-2 py-2 rounded bg-white/5 border border-white/15 cursor-pointer select-none"
                  title="Hides CI polling (sync-ci), analytics pageviews, and bulk result uploads — machine traffic that otherwise buries human activity"
                >
                  <input
                    type="checkbox"
                    checked={!!auditSearch.hideNoise}
                    onChange={(e) => {
                      const hideNoise = e.target.checked;
                      setAuditSearch(s => ({ ...s, hideNoise, skip: 0 }));
                      submitAuditSearch({ hideNoise, skip: 0 });
                    }}
                    className="rounded"
                  />
                  Hide automation noise
                </label>
              </div>

              <div className="mt-3 overflow-x-auto overflow-y-auto rounded border border-white/10 max-h-[65vh]">
                <table className="w-full text-xs min-w-[1100px] table-fixed">
                  <colgroup>
                    <col className="w-[160px]" />
                    <col className="w-[140px]" />
                    <col className="w-[200px]" />
                    <col className="w-[72px]" />
                    <col className="w-[64px]" />
                    <col className="w-[120px]" />
                    <col />
                    <col className="w-[72px]" />
                  </colgroup>
                  <thead className="text-slate-400 sticky top-0 bg-surface-1 z-10">
                    <tr className="text-left">
                      <th className="px-2 py-2 whitespace-nowrap">Time</th>
                      <th className="px-2 py-2 whitespace-nowrap">Tenant</th>
                      <th className="px-2 py-2 whitespace-nowrap">User</th>
                      <th className="px-2 py-2 whitespace-nowrap">Method</th>
                      <th className="px-2 py-2 whitespace-nowrap">Status</th>
                      <th className="px-2 py-2 whitespace-nowrap">Entity</th>
                      <th className="px-2 py-2 whitespace-nowrap">Path</th>
                      <th className="px-2 py-2 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(adminAudit.logs || []).map(l => (
                      <tr key={l._id} className="border-t border-white/10 hover:bg-white/[0.04]">
                        <td className="px-2 py-1.5 whitespace-nowrap align-top text-slate-400">{l.createdAt ? new Date(l.createdAt).toLocaleString() : '—'}</td>
                        <td className="px-2 py-1.5 align-top">
                          <div className="text-slate-200 truncate" title={l.tenantName || l.tenantSlug || ''}>{l.tenantName || l.tenantSlug || (l.tenantId ? String(l.tenantId).slice(-6) : '—')}</div>
                          {(l.tenantSlug || l.tenantId) && (
                            <div className="text-slate-500 truncate" title={[l.tenantSlug, l.tenantId].filter(Boolean).join(' • ')}>
                              {l.tenantSlug ? l.tenantSlug : ''}{l.tenantSlug && l.tenantId ? ' • ' : ''}{l.tenantId ? String(l.tenantId).slice(-6) : ''}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 align-top truncate" title={l.userEmail || ''}>{l.userEmail || '—'}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap align-top">{l.method}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap align-top">{l.statusCode}</td>
                        <td className="px-2 py-1.5 align-top truncate" title={`${l.entityType || ''}${l.entityId ? `:${l.entityId}` : ''}`}>
                          {l.entityType || '—'}{l.entityId ? `:${String(l.entityId).slice(-6)}` : ''}
                        </td>
                        <td className="px-2 py-1.5 align-top font-mono text-[11px] text-slate-400 whitespace-nowrap truncate" title={l.path || ''}>{l.path || '—'}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap align-top">
                          <button
                            onClick={() => openAuditDiff(l)}
                            className="text-[11px] px-2 py-1 rounded bg-white/5 border border-white/15 hover:border-neutral-500"
                          >Diff</button>
                        </td>
                      </tr>
                    ))}
                    {(!adminAudit.logs || adminAudit.logs.length === 0) && (
                      <tr><td colSpan={8} className="px-2 py-6 text-center text-slate-500">{auditLoading ? 'Loading…' : 'No results'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-1 text-[11px] text-slate-500">Scroll horizontally to see all columns. Hover Path / User for full values.</div>

              <div className="mt-3 flex items-center justify-between text-xs">
                <div className="text-slate-400">Total: {adminAudit.total || 0}</div>
                <div className="flex items-center gap-2">
                  <button
                    disabled={(auditSearch.skip || 0) === 0 || auditLoading}
                    onClick={() => submitAuditSearch({ skip: Math.max(0, (auditSearch.skip || 0) - (auditSearch.limit || 100)) })}
                    className="text-xs px-2 py-1 rounded bg-white/5 border border-white/15 disabled:opacity-50"
                  >Prev</button>
                  <button
                    disabled={auditLoading || ((auditSearch.skip || 0) + (auditSearch.limit || 100) >= (adminAudit.total || 0))}
                    onClick={() => submitAuditSearch({ skip: (auditSearch.skip || 0) + (auditSearch.limit || 100) })}
                    className="text-xs px-2 py-1 rounded bg-white/5 border border-white/15 disabled:opacity-50"
                  >Next</button>
                </div>
              </div>
            </div>

            {selectedAuditLog && (
            <div className="min-w-0 app-panel p-4 xl:sticky xl:top-4 xl:self-start">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">Diff</h2>
                <button
                  onClick={() => { setSelectedAuditLog(null); setAuditDiff(null); }}
                  className="text-xs px-2 py-1 rounded bg-white/5 border border-white/15"
                >Clear</button>
              </div>

              <div className="mt-3 space-y-2 text-xs">
                  <div className="text-slate-400 break-all">{selectedAuditLog.method} {selectedAuditLog.path}</div>
                  <div className="text-slate-400">tenant: <span className="text-slate-200">{selectedAuditLog.tenantName || selectedAuditLog.tenantSlug || (selectedAuditLog.tenantId ? String(selectedAuditLog.tenantId) : '—')}</span></div>
                  <div className="text-slate-400">status: <span className="text-slate-200">{selectedAuditLog.statusCode}</span></div>
                  {auditDiffLoading && <div className="text-xs text-slate-500">Loading diff…</div>}

                  {auditDiff && (
                    <>
                      {auditDiff.available === false && (
                        <div className="text-xs text-amber-300">Diff not available{auditDiff.reason ? `: ${auditDiff.reason}` : ''}{auditDiff.error ? `: ${auditDiff.error}` : ''}</div>
                      )}

                      {auditDiff.available && (
                        <>
                          <div className="text-slate-400">Changes</div>
                          <div className="max-h-48 overflow-auto rounded border border-white/10">
                            <table className="w-full text-xs min-w-[520px]">
                              <thead className="text-slate-400 sticky top-0 bg-surface-1">
                                <tr className="text-left">
                                  <th className="px-2 py-1">Field</th>
                                  <th className="px-2 py-1">Before</th>
                                  <th className="px-2 py-1">After</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(auditDiff.changes || []).map((c, idx) => (
                                  <tr key={idx} className="border-t border-white/10">
                                    <td className="px-2 py-1 font-mono">{c.field}</td>
                                    <td className="px-2 py-1 font-mono break-all">{typeof c.before === 'object' ? JSON.stringify(c.before) : String(c.before != null ? c.before : '')}</td>
                                    <td className="px-2 py-1 font-mono break-all">{typeof c.after === 'object' ? JSON.stringify(c.after) : String(c.after != null ? c.after : '')}</td>
                                  </tr>
                                ))}
                                {(!auditDiff.changes || auditDiff.changes.length === 0) && (
                                  <tr><td colSpan={3} className="px-2 py-3 text-slate-500">No field-level changes detected.</td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          <details className="mt-2">
                            <summary className="cursor-pointer text-slate-400">Raw patch (captured request body)</summary>
                            <pre className="mt-2 p-2 rounded bg-[#050807] border border-white/10 overflow-auto max-h-64">{JSON.stringify(auditDiff.patch || {}, null, 2)}</pre>
                          </details>
                        </>
                      )}
                    </>
                  )}
                </div>
            </div>
            )}
          </div>
        )}

        {adminTab === 'accounts' && (
          <>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              {/* min-w-0 lets the table scroll inside the grid column instead of clipping */}
              <div className="xl:col-span-2 min-w-0 app-panel p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold">Accounts</h2>
                  <button
                    onClick={fetchTenants}
                    disabled={loading}
                    className="text-xs px-2 py-1 rounded bg-white/5 border border-white/15 disabled:opacity-60 inline-flex items-center gap-1.5"
                  >
                    {loading && <span className="inline-block h-3 w-3 rounded-full border-2 border-slate-500 border-t-slate-200 animate-spin" aria-hidden="true" />}
                    {loading ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>
                <div className="overflow-x-auto rounded border border-white/10">
                  <table className="w-full text-sm min-w-[760px]">
                    <thead className="bg-surface-1 sticky top-0">
                      <tr className="text-left text-xs text-slate-400">
                        <th className="py-2 px-2">Name</th>
                        <th className="py-2 px-2">Status</th>
                        <th className="py-2 px-2">Plan</th>
                        <th className="py-2 px-2">Seats</th>
                        <th className="py-2 px-2">AI Credits</th>
                        <th className="py-2 px-2">Features</th>
                        <th className="py-2 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants.map((t, rowIdx) => {
                        const plan = normPlan(t);
                        const isSaving = isSavingTenant(tenantKey(t));
                        return (
                          <tr key={tenantKey(t) || `tenant-row-${rowIdx}`} className="border-t border-white/10">
                            <td className="py-3 px-2">
                              {renamingTenantId === tenantKey(t) ? (
                                <div className="flex items-center gap-1.5">
                                  <input
                                    autoFocus
                                    value={renameValue}
                                    disabled={isSaving}
                                    maxLength={80}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleRenameTenant(t);
                                      if (e.key === 'Escape') cancelRenameTenant();
                                    }}
                                    className="text-sm px-2 py-1 rounded bg-white/5 border border-white/20 w-40"
                                  />
                                  <button
                                    onClick={() => handleRenameTenant(t)}
                                    disabled={isSaving}
                                    className="text-xs px-2 py-1 rounded border bg-emerald-700/30 border-emerald-700 text-teal-300 disabled:opacity-60"
                                  >{isSaving ? '…' : 'Save'}</button>
                                  <button
                                    onClick={cancelRenameTenant}
                                    disabled={isSaving}
                                    className="text-xs px-2 py-1 rounded border bg-white/5 border-white/15 text-slate-400"
                                  >Cancel</button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5 group">
                                  <div className="min-w-0">
                                    <button className="underline underline-offset-2" onClick={()=>{ setSelectedTenant(t); fetchTenantUsers(tenantKey(t)); }}>{t.name || t.slug || t._id}</button>
                                    {t.slug && <div className="text-[11px] text-slate-500">{t.slug}</div>}
                                  </div>
                                  <button
                                    onClick={() => startRenameTenant(t)}
                                    className="text-[11px] px-1.5 py-0.5 rounded border bg-white/5 border-white/15 text-slate-400 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                                    title="Rename tenant"
                                  >Rename</button>
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-2 text-xs">{t.blocked ? <span className="text-red-400">Blocked</span> : <span className="text-teal-400">Active</span>}</td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <select
                                  className="text-xs px-2 py-1.5 rounded bg-white/5 border border-white/15"
                                  value={plan}
                                  disabled={isSaving}
                                  onChange={(e) => handleSetPlan(t, e.target.value)}
                                >
                                  {PLAN_OPTIONS.map(p => (
                                    <option key={p.value} value={p.value}>{p.label}</option>
                                  ))}
                                </select>
                                {isSaving && <span className="text-[11px] text-slate-500">Saving…</span>}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-xs">
                              <span>{t.seats || 1}</span>
                            </td>
                            <td className="py-3 px-2 text-xs">
                              <div className="flex items-center gap-1.5 whitespace-nowrap">
                                <span title="Used this period / prepaid balance">
                                  {(t.aiCredits?.used || 0)} used
                                  <span className="text-slate-500"> · {(t.aiCredits?.purchased || 0)} prepaid</span>
                                </span>
                                <button
                                  onClick={() => handleGrantAiCredits(t)}
                                  disabled={isSaving}
                                  className="text-[11px] px-1.5 py-0.5 rounded border bg-white/5 border-white/15 text-slate-400 hover:border-white/20 disabled:opacity-60"
                                  title="Grant AI credits"
                                >+</button>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-xs">
                              <div className="flex gap-2 flex-wrap">
                                {CURATED_FLAGS.map(flag => {
                                  const v = getTenantFeatureValue(t, flag.key);
                                  const disabled = isSavingTenant(tenantKey(t));
                                  if (flag.type === 'enum') {
                                    return (
                                      <div key={flag.key} className="flex items-center gap-2">
                                        <span className="text-[11px] text-slate-400 whitespace-nowrap">{flag.label}:</span>
                                        <select
                                          value={String(v || 'standard')}
                                          disabled={disabled}
                                          onChange={(e) => updateTenantFeaturesCurated(t, { [flag.key]: e.target.value })}
                                          className="text-[11px] px-2 py-1 rounded bg-white/5 border border-white/15"
                                        >
                                          {flag.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                      </div>
                                    );
                                  }
                                  const on = !!v;
                                  return (
                                    <button
                                      key={flag.key}
                                      disabled={disabled}
                                      onClick={() => updateTenantFeaturesCurated(t, { [flag.key]: !on })}
                                      className={`px-2 py-1 rounded text-[11px] border ${on ? 'bg-teal-500/15 border-teal-500/40 text-teal-300' : 'bg-white/5 border-white/15 text-slate-400'} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                                      title={flag.label}
                                    >
                                      {flag.key}
                                    </button>
                                  );
                                })}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  onClick={()=>handleToggleBlock(t)}
                                  disabled={isSaving}
                                  className={`text-xs px-2 py-1.5 rounded border ${isSaving ? 'bg-surface-1 border-white/10 text-slate-600 cursor-not-allowed' : 'bg-white/5 border-white/15 hover:border-white/20'}`}
                                >{t.blocked ? 'Unblock' : 'Block'}</button>
                                <button
                                  disabled={isSaving}
                                  onClick={()=>handleAdjustSeats(t, +1)}
                                  className={`text-xs px-2 py-1.5 rounded border ${!isSaving ? 'bg-white/5 border-white/15 hover:border-white/20' : 'bg-surface-1 border-white/10 text-slate-600 cursor-not-allowed'}`}
                                  title={isSaving ? 'Saving…' : 'Increase seats'}
                                >+ Seat</button>
                                <button
                                  disabled={isSaving}
                                  onClick={()=>handleAdjustSeats(t, -1)}
                                  className={`text-xs px-2 py-1.5 rounded border ${!isSaving ? 'bg-white/5 border-white/15 hover:border-white/20' : 'bg-surface-1 border-white/10 text-slate-600 cursor-not-allowed'}`}
                                  title={isSaving ? 'Saving…' : 'Decrease seats'}
                                >- Seat</button>
                                {isSavedTenant(tenantKey(t)) && (
                                  <span className="text-[11px] text-teal-400 whitespace-nowrap">Saved ✓</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {tenants.length === 0 && (
                        <tr>
                          <td colSpan="6" className="py-6 text-center text-slate-500">No accounts found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="min-w-0 app-panel p-4">
                <h2 className="text-sm font-semibold mb-3">Users {selectedTenant ? `• ${selectedTenant.name || selectedTenant._id}` : ''}</h2>
                {selectedTenant ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs text-slate-400">
                        Plan: <span className="text-slate-200">{normPlan(selectedTenant)}</span>
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {tenantUsers.length} user{tenantUsers.length === 1 ? '' : 's'}
                      </div>
                    </div>

                    {/* NEW: Billing / Subscription control */}
                    <div className="p-3 app-panel">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold">Billing / Subscription</div>
                        <button
                          disabled={billingBusy}
                          onClick={() => refreshSelectedTenantPanels(selectedTenant)}
                          className="text-[11px] px-2 py-1 rounded bg-white/5 border border-white/15"
                        >Refresh</button>
                      </div>

                      {(() => {
                        const su = seatUsageByTenantId[String(selectedTenant._id)];
                        const usage = su?.usage;
                        const seats = su?.seats ?? selectedTenant?.seats;
                        const remaining = su?.remaining ?? (seats != null ? Math.max(0, seats - (usage?.total || 0)) : null);
                        const sub = su?.subscription;
                        const displayPlan = su?.plan || sub?.plan || normPlan(selectedTenant);
                        const displayStatus = sub?.status || (displayPlan && displayPlan !== 'free' ? 'active' : '—');
                        const tempUntil = selectedTenant?.features?.tempAccessUntil;
                        const seatDraftNum = parseInt(seatLimitDraft, 10);
                        const seatDirty = Number.isFinite(seatDraftNum) && seatDraftNum !== Number(selectedTenant?.seats || 0);
                        return (
                          <div className="space-y-2 text-xs text-slate-300">
                            <div className="grid grid-cols-2 gap-2">
                              <div className="p-2 rounded bg-white/[0.04] border border-white/10">
                                <div className="text-slate-400">Current plan</div>
                                <div className="text-slate-200">{displayPlan}</div>
                              </div>
                              <div className="p-2 rounded bg-white/[0.04] border border-white/10">
                                <div className="text-slate-400">Seat usage</div>
                                <div className="text-slate-200">
                                  {usage ? `${usage.total} total (${usage.users} users, ${usage.pending} invites)` : '—'}
                                </div>
                                <div className="text-slate-400">
                                  Limit: {seats ? seats : '—'}{seats && remaining !== null ? ` • Remaining: ${remaining}` : ''}
                                </div>
                              </div>
                            </div>

                            <div className="p-2 rounded bg-white/[0.04] border border-white/10">
                              <div className="text-slate-400 mb-2">Seat limit</div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <input
                                  type="number"
                                  min={1}
                                  max={10000}
                                  value={seatLimitDraft}
                                  onChange={(e) => setSeatLimitDraft(e.target.value)}
                                  className="w-24 text-xs px-2 py-1 rounded bg-white/5 border border-white/15 text-slate-200"
                                  title="Tenant seat limit"
                                />
                                <button
                                  disabled={billingBusy || !seatDirty}
                                  onClick={handleSetSeatLimit}
                                  className="text-xs px-2 py-1 rounded border bg-teal-500/15 border-teal-500/40 text-teal-300 disabled:opacity-50"
                                >{billingBusy ? 'Saving…' : 'Save seats'}</button>
                              </div>
                              <div className="mt-1 text-[11px] text-slate-500">
                                This sets the account seat limit (currently {selectedTenant?.seats || '—'}).
                              </div>
                            </div>

                            <div className="p-2 rounded bg-white/[0.04] border border-white/10">
                              <div className="text-slate-400">Subscription</div>
                              <div className="text-slate-200">
                                {`${displayPlan || '—'} • ${displayStatus || '—'}${sub?.currentPeriodEnd ? ` • Renews: ${new Date(sub.currentPeriodEnd).toLocaleDateString()}` : ''}`}
                              </div>
                              {sub?.email && <div className="text-slate-400">Owner: {sub.email}</div>}
                            </div>

                            <div className="p-2 rounded bg-white/[0.04] border border-white/10">
                              <div className="text-slate-400 mb-1">Invoice links</div>
                              <button
                                disabled={billingBusy}
                                onClick={handleOpenBillingPortal}
                                className="text-xs px-2 py-1 rounded bg-white/5 border border-white/15"
                                title="Opens Stripe-hosted billing portal (invoices, payment method, etc.)"
                              >Open billing portal</button>
                            </div>

                            <div className="p-2 rounded bg-white/[0.04] border border-white/10">
                              <div className="text-slate-400 mb-2">Grant temporary access</div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {(() => {
                                  const normDays = Math.max(1, Math.min(90, parseInt(tempAccessDays || '7', 10) || 7));
                                  const granted = lastGrantedDays === normDays;
                                  return (
                                    <>
                                      <label className="text-[11px] text-slate-500 whitespace-nowrap">Days</label>
                                      <input
                                        type="number"
                                        min={1}
                                        max={90}
                                        value={tempAccessDays}
                                        onChange={(e) => setTempAccessDays(e.target.value)}
                                        className="w-20 text-xs px-2 py-1 rounded bg-white/5 border border-white/15 text-slate-200"
                                        title="Number of days to grant temporary access (not seat count)"
                                      />
                                      <button
                                        disabled={billingBusy || granted}
                                        onClick={handleGrantTemporaryAccess}
                                        className={`text-xs px-2 py-1 rounded border ${granted
                                          ? 'bg-white/5 border-white/15 text-slate-500 cursor-default'
                                          : 'bg-teal-500/15 border-teal-500/40 text-teal-300'} disabled:opacity-70`}
                                      >{billingBusy ? 'Working…' : granted ? 'Granted ✓' : 'Grant'}</button>
                                      <button
                                        disabled={billingBusy}
                                        onClick={handleRevokeTemporaryAccess}
                                        className="text-xs px-2 py-1 rounded bg-white/5 border border-white/15 disabled:opacity-70"
                                      >{flashedActions.revokeTemp ? 'Revoked ✓' : 'Revoke'}</button>
                                    </>
                                  );
                                })()}
                              </div>
                              <div className="mt-2 text-[11px] text-slate-400">
                                {tempUntil ? `Temporary access until: ${new Date(tempUntil).toLocaleString()}` : 'No temporary access set.'}
                              </div>
                              <div className="mt-1 text-[11px] text-slate-500">
                                Days only — this does not change the seat limit.
                              </div>
                            </div>

                            <div className="p-2 rounded bg-amber-500/10 border border-amber-600/40">
                              <div className="text-amber-200 text-[11px] font-semibold mb-1">Seat enforcement tools</div>
                              <div className="text-amber-200/80 text-[11px]">
                                Deactivate users and revoke expired invites to free up seats. Changes may remove access immediately.
                              </div>
                              <div className="mt-2">
                                <button
                                  disabled={billingBusy}
                                  onClick={handleBulkRevokeExpiredInvites}
                                  className="text-xs px-2 py-1 rounded bg-surface-1 border border-amber-600/40 text-amber-200 disabled:opacity-70"
                                >{flashedActions.revokeInvites ? 'Revoked ✓' : 'Revoke expired invites'}</button>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Branding & subdomain */}
                    <div className="p-3 app-panel">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold">Branding &amp; Subdomain</div>
                        <button
                          disabled={brandingSaving || !brandingDirty}
                          onClick={handleSaveBranding}
                          className={`text-[11px] px-2 py-1 rounded border ${brandingDirty
                            ? 'bg-teal-500/15 border-teal-500/40 text-teal-300'
                            : 'bg-white/5 border-white/15 text-slate-500 cursor-default'} disabled:opacity-70`}
                        >{brandingSaving ? 'Saving…' : brandingDirty ? 'Save' : 'Saved ✓'}</button>
                      </div>
                      <div className="space-y-2 text-xs">
                        <label className="flex items-center gap-2 text-slate-300">
                          <input
                            type="checkbox"
                            checked={brandingForm.subdomainEnabled}
                            onChange={(e) => setBrandingForm(f => ({ ...f, subdomainEnabled: e.target.checked }))}
                            className="accent-emerald-500"
                          />
                          Enable branded subdomain
                        </label>
                        {selectedTenant?.slug && (
                          <div className="text-[11px] text-slate-400">
                            Portal URL:{' '}
                            <a
                              href={`https://${selectedTenant.slug}.testera.io`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-teal-400 hover:text-teal-300 underline underline-offset-2"
                            >{selectedTenant.slug}.testera.io</a>
                            {' '}(live as soon as it&apos;s enabled — no deploy needed)
                          </div>
                        )}
                        <div>
                          <div className="text-slate-400 mb-1">Display name</div>
                          <input
                            type="text"
                            value={brandingForm.displayName}
                            onChange={(e) => setBrandingForm(f => ({ ...f, displayName: e.target.value }))}
                            placeholder={selectedTenant?.name || 'Company name'}
                            className="w-full px-2 py-1.5 rounded bg-white/5 border border-white/15 text-slate-200"
                          />
                        </div>
                        <div>
                          <div className="text-slate-400 mb-1">Logo URL (https, square works best)</div>
                          <input
                            type="url"
                            value={brandingForm.logoUrl}
                            onChange={(e) => setBrandingForm(f => ({ ...f, logoUrl: e.target.value }))}
                            placeholder="https://…/logo.png"
                            className="w-full px-2 py-1.5 rounded bg-white/5 border border-white/15 text-slate-200"
                          />
                        </div>
                        <div>
                          <div className="text-slate-400 mb-1">Accent color (optional, e.g. #6d28d9)</div>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={brandingForm.accentColor}
                              onChange={(e) => setBrandingForm(f => ({ ...f, accentColor: e.target.value }))}
                              placeholder="#334155"
                              className="flex-1 px-2 py-1.5 rounded bg-white/5 border border-white/15 text-slate-200"
                            />
                            {/^#[0-9a-fA-F]{3,8}$/.test(brandingForm.accentColor) && (
                              <span className="w-6 h-6 rounded border border-white/15" style={{ backgroundColor: brandingForm.accentColor }} />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* User list with deactivate action */}
                    <div className="rounded-lg border border-white/10 overflow-hidden">
                      <div className="px-3 py-2 bg-surface-2 border-b border-white/10 text-xs text-slate-400 flex items-center justify-between">
                        <span>User list</span>
                        <span className="text-[11px] text-slate-500">Scroll</span>
                      </div>
                      <div className="max-h-[46vh] overflow-auto">
                        <div className="p-2 space-y-2">
                          {tenantUsers.map(u => (
                            <div key={u._id} className="p-3 rounded border border-white/10 bg-white/[0.04]">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="text-sm truncate">
                                    {u.name}
                                    <span className="text-xs text-slate-400"> {u.email}</span>
                                  </div>
                                  <div className="text-xs text-slate-400">Role: {u.role || 'user'} • Status: {u.status || 'active'}</div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <button
                                    disabled={billingBusy || String(u.status || 'active').toLowerCase() === 'inactive'}
                                    onClick={() => handleDeactivateUser(u)}
                                    className={`text-xs px-2 py-1.5 rounded border ${String(u.status || 'active').toLowerCase() === 'inactive' ? 'bg-surface-1 border-white/10 text-slate-600 cursor-not-allowed' : 'bg-surface-1 border-red-600/40 text-red-200 hover:border-red-500/60'}`}
                                    title="Deactivate user (may free up a seat)"
                                  >Deactivate</button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {tenantUsers.length === 0 && <p className="text-xs text-slate-500 p-2">No users.</p>}
                        </div>
                      </div>
                    </div>
                    {tenantUsers.length === 0 && <p className="text-xs text-slate-500">No users.</p>}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">Select an account to view users.</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <AICompanionModal
        isOpen={showCompanion}
        onClose={() => setShowCompanion(false)}
        mode="admin"
        tenantId={currentUser?.tenant}
        userId={currentUser?._id || currentUser?.id}
      />
    </div>
  );
};

export default AdminPortal;